import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { settingsService } from '../../services/settings-service.js';

export class ApiSettings extends LitElement {
  static properties = {
    _webhooks: { type: Array, state: true },
    _saving: { state: true },
    _visionKey: { state: true },
    _visionEnabled: { state: true },
    _visionHasKey: { state: true },
    _visionSaving: { state: true },
    _visionTesting: { state: true },
    _visionTestResult: { state: true },
  };

  static styles = [designSystem, componentStyles, settingsStyles];

  constructor() {
    super();
    this._webhooks = [];
    this._saving = false;
    this._visionKey = '';
    this._visionEnabled = false;
    this._visionHasKey = false;
    this._visionSaving = false;
    this._visionTesting = false;
    this._visionTestResult = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    try {
      const [whData, visionData] = await Promise.all([
        settingsService.loadWebhooksConfig(),
        settingsService.loadVisionConfig(),
      ]);
      if (whData.success) {
        this._webhooks = whData.webhooks || [];
      }
      if (visionData.success) {
        this._visionKey = visionData.config?.voice_google_vision_api_key || '';
        this._visionEnabled = visionData.config?.voice_google_vision_enabled === '1';
        this._visionHasKey = visionData.config?.has_key || false;
      }
    } catch (err) {
      console.error('[ApiSettings] Load failed:', err);
    }
  }

  _addWebhook() {
    this._webhooks = [...this._webhooks, {
      id: Date.now(),
      url: '',
      events: ['call.started', 'call.ended']
    }];
  }

  _removeWebhook(id) {
    this._webhooks = this._webhooks.filter(w => w.id !== id);
  }

  _updateWebhookUrl(id, url) {
    this._webhooks = this._webhooks.map(w =>
      w.id === id ? { ...w, url } : w
    );
  }

  _toggleWebhookEvent(id, event) {
    this._webhooks = this._webhooks.map(w => {
      if (w.id !== id) return w;
      const events = w.events.includes(event)
        ? w.events.filter(e => e !== event)
        : [...w.events, event];
      return { ...w, events };
    });
  }

  async _save() {
    if (this._saving) return;
    this._saving = true;

    try {
      const data = await settingsService.saveWebhooksConfig({ webhooks: this._webhooks });
      if (data.success) {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.message || 'Webhooks saved' }, bubbles: true, composed: true }));
        this.dispatchEvent(new CustomEvent('settings-saved', { bubbles: true, composed: true }));
      } else {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.error || 'Save failed', type: 'error' }, bubbles: true, composed: true }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Network error saving webhooks', type: 'error' }, bubbles: true, composed: true }));
    } finally {
      this._saving = false;
    }
  }

  async _saveVision() {
    if (this._visionSaving) return;
    this._visionSaving = true;

    try {
      const payload = { voice_google_vision_enabled: this._visionEnabled ? 1 : 0 };
      // Only send key if user typed a new one (not the masked value)
      const keyInput = this.renderRoot?.querySelector('#vision-api-key');
      const keyVal = keyInput?.value || '';
      if (keyVal && !keyVal.startsWith('****')) {
        payload.voice_google_vision_api_key = keyVal;
      }

      const data = await settingsService.saveVisionConfig(payload);
      if (data.success) {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Vision settings saved' }, bubbles: true, composed: true }));
        this._visionHasKey = true;
        this._visionTestResult = null;
        await this._load();
      } else {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.error || 'Save failed', type: 'error' }, bubbles: true, composed: true }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Network error saving vision settings', type: 'error' }, bubbles: true, composed: true }));
    } finally {
      this._visionSaving = false;
    }
  }

  async _testVision() {
    if (this._visionTesting) return;
    this._visionTesting = true;
    this._visionTestResult = null;

    try {
      const data = await settingsService.testVisionConnection();
      if (data.success) {
        this._visionTestResult = { ok: true, message: 'Connected successfully' };
      } else {
        this._visionTestResult = { ok: false, message: data.error || 'Connection failed' };
      }
    } catch (err) {
      this._visionTestResult = { ok: false, message: 'Network error testing connection' };
    } finally {
      this._visionTesting = false;
    }
  }

  render() {
    const allEvents = [
      { value: 'call.started', label: 'Call Started' },
      { value: 'call.ended', label: 'Call Ended' },
      { value: 'call.failed', label: 'Call Failed' },
      { value: 'transcript.ready', label: 'Transcript Ready' },
      { value: 'summary.ready', label: 'Summary Ready' },
      { value: 'agent.error', label: 'Agent Error' }
    ];

    return html`
      ${iconStyles}
      <div class="content-header">
        <h2 class="content-title">API & Webhooks</h2>
        <p class="content-description">Manage API keys for external integrations and configure webhook endpoints for event notifications.</p>
      </div>

      <!-- API Keys Section -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-key section-icon"></i>
          <span class="section-title">API Keys</span>
        </div>
        <p class="section-description" style="margin-top:var(--space-1);">Use API keys to authenticate requests to the Voice AI API.</p>

        <div class="empty-state">
          <i class="fa-sharp fa-light fa-key"></i>
          <p>No API keys configured yet.</p>
          <p style="font-size:var(--text-xs);">API key management will be available in a future update.</p>
        </div>
      </div>

      <!-- Google Vision Section -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-eye section-icon"></i>
          <span class="section-title">Google Cloud Vision</span>
        </div>
        <p class="section-description" style="margin-top:var(--space-1);">Enable Google Vision API for accurate OCR text extraction and image analysis on uploaded knowledgebase images.</p>

        <div class="form-group" style="margin-top:var(--space-3);">
          <label class="form-label">API Key</label>
          <input type="password" class="form-input" id="vision-api-key"
            .value=${this._visionKey}
            placeholder=${this._visionHasKey ? '****' : 'Enter Google Vision API key'}
            style="max-width:500px;"
          />
          <p class="form-hint" style="margin-top:var(--space-1);font-size:var(--text-xs);color:var(--text-secondary);">
            Get a key from the <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener" style="color:var(--primary);">Google Cloud Console</a>.
            Enable the "Cloud Vision API" for your project.
          </p>
        </div>

        <div class="form-group" style="margin-top:var(--space-3);">
          <label class="toggle-label" style="display:flex;align-items:center;gap:var(--space-2);cursor:pointer;">
            <input type="checkbox"
              .checked=${this._visionEnabled}
              @change=${e => { this._visionEnabled = e.target.checked; }}
              style="width:18px;height:18px;"
            />
            <span>Enable auto-analysis of uploaded knowledgebase images</span>
          </label>
          <p class="form-hint" style="margin-top:var(--space-1);font-size:var(--text-xs);color:var(--text-secondary);">
            When enabled, images uploaded to agent knowledgebases are automatically analyzed for text and labels.
            Analysis results are used instead of raw images for more accurate AI responses.
          </p>
        </div>

        <div style="display:flex;gap:var(--space-2);margin-top:var(--space-3);align-items:center;">
          <button class="btn btn-primary btn-sm" @click=${this._saveVision} ?disabled=${this._visionSaving}>
            ${this._visionSaving
              ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Saving...`
              : html`<i class="fa-sharp fa-light fa-floppy-disk"></i> Save Vision Settings`
            }
          </button>
          <button class="btn btn-secondary btn-sm" @click=${this._testVision} ?disabled=${this._visionTesting || !this._visionHasKey}>
            ${this._visionTesting
              ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Testing...`
              : html`<i class="fa-sharp fa-light fa-plug"></i> Test Connection`
            }
          </button>
          ${this._visionTestResult ? html`
            <span style="font-size:var(--text-sm);color:${this._visionTestResult.ok ? 'var(--success, #22c55e)' : 'var(--danger, #ef4444)'};">
              <i class="fa-sharp fa-solid ${this._visionTestResult.ok ? 'fa-check-circle' : 'fa-times-circle'}"></i>
              ${this._visionTestResult.message}
            </span>
          ` : ''}
        </div>
      </div>

      <!-- Webhooks Section -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-webhook section-icon"></i>
          <span class="section-title">Webhook Endpoints</span>
        </div>
        <p class="section-description">Configure URLs to receive real-time event notifications.</p>

        ${this._webhooks.map(wh => html`
          <div class="webhook-item">
            <div class="webhook-header">
              <div class="form-group" style="flex:1;margin-right:var(--space-3);">
                <label class="form-label">Endpoint URL</label>
                <input type="url" class="form-input"
                  .value=${wh.url}
                  placeholder="https://your-server.com/webhook"
                  @input=${e => this._updateWebhookUrl(wh.id, e.target.value)}
                />
              </div>
              <button class="btn btn-secondary btn-sm" style="align-self:flex-end;" @click=${() => this._removeWebhook(wh.id)}>
                <i class="fa-sharp fa-light fa-trash"></i>
                Remove
              </button>
            </div>
            <div>
              <label class="form-label" style="margin-bottom:var(--space-2);display:block;">Event Types</label>
              <div class="checkbox-group">
                ${allEvents.map(evt => html`
                  <label class="checkbox-item">
                    <input type="checkbox"
                      ?checked=${wh.events.includes(evt.value)}
                      @change=${() => this._toggleWebhookEvent(wh.id, evt.value)}
                    />
                    ${evt.label}
                  </label>
                `)}
              </div>
            </div>
          </div>
        `)}

        <button class="btn btn-secondary btn-sm" style="margin-top:var(--space-2);" @click=${this._addWebhook}>
          <i class="fa-sharp fa-light fa-plus"></i>
          Add Webhook Endpoint
        </button>
      </div>

      <div class="actions-bar">
        <button class="btn btn-primary" @click=${this._save} ?disabled=${this._saving}>
          ${this._saving
            ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Saving...`
            : html`<i class="fa-sharp fa-light fa-floppy-disk"></i> Save Changes`
          }
        </button>
      </div>
    `;
  }
}

customElements.define('api-settings', ApiSettings);
